import React, { useState } from 'react';
import { Building2, Users, CheckCircle, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import DataTable from '../../../../components/ui/DataTable';
import { useDepartments } from '../../hooks/useDepartments';
import { Department } from '../../../../types/organisation';
import OrganisationNavigation from '../OrganisationNavigation';
import DepartmentDetailForm from './DepartmentDetailForm';

const DepartmentManagement: React.FC = () => {
  const { departments, loading, refresh, deleteDepartment } = useDepartments();
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Department[]>([]);

  const columns = [
    { key: 'departmentCode', title: 'Code', sortable: true },
    { key: 'departmentName', title: 'Nom du département', sortable: true },
    {
      key: 'site',
      title: 'Site',
      render: (value: unknown) => value?.siteName || 'Non défini',
    },
    {
      key: 'manager',
      title: 'Manager',
      render: (value: unknown) => value?.fullName || 'Non assigné',
    },
    {
      key: 'employeeCount',
      title: 'Employés',
      render: (value: number) => value || 0,
    },
    {
      key: 'isActive',
      title: 'Statut',
      render: (value: boolean) => value ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Actif</span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactif</span>
      ),
    },
  ];

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return;
    if (!window.confirm(`Supprimer ${selectedRows.length} département(s) ?`)) return;
    try {
      await Promise.all(selectedRows.map(d => deleteDepartment(d.id)));
      setSelectedRows([]);
      await refresh();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleClose = async () => {
    setSelectedId(undefined);
    await refresh();
  };

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 rounded-md p-2 md:p-3">
              <Building2 className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {departments?.filter(d => d.isActive).length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Départements actifs</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-2 md:p-3">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {departments?.length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Total départements</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-500 rounded-md p-2 md:p-3">
              <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {departments?.filter(d => d.manager).length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Avec manager</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-orange-500 rounded-md p-2 md:p-3">
              <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {departments?.filter(d => !d.manager).length || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Sans manager</div>
            </div>
          </div>
        </div>
      </div>

      {/* En-tête + Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Building2 className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Départements</h1>
            <p className="text-gray-600">Organisez vos départements par site</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRows.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              <span>Supprimer ({selectedRows.length})</span>
            </button>
          )}
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Nouveau département</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <DataTable
          data={departments || []}
          columns={columns}
          loading={loading}
          selectable={true}
          onSelectionChange={setSelectedRows}
          onRowDoubleClick={(dept) => setSelectedId(dept.id)}
          emptyMessage="Aucun département trouvé"
        />
      </div>

      {/* Formulaire de détail */}
      {selectedId !== undefined && (
        <DepartmentDetailForm
          departmentId={selectedId}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default DepartmentManagement;
